import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Upload, Download, RotateCcw } from 'lucide-react';

const PointInput = ({ 
  onAddPoint, 
  onImportPoints, 
  onExportPoints, 
  onClearPoints,
  coordinateSystem,
  onCoordinateSystemChange 
}) => {
  const [pointData, setPointData] = useState({ id: '', x: '', y: '', z: '' });
  const [importText, setImportText] = useState('');
  const [showImport, setShowImport] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pointData.x && pointData.y && pointData.z) {
      onAddPoint({
        id: pointData.id || `P${Date.now()}`,
        x: parseFloat(pointData.x),
        y: parseFloat(pointData.y),
        z: parseFloat(pointData.z)
      });
      setPointData({ id: '', x: '', y: '', z: '' });
    }
  };

  const handleImport = () => {
    const lines = importText.trim().split('\n');
    const points = [];
    
    lines.forEach((line, index) => {
      const parts = line.trim().split(/[\s,;]+/);
      if (parts.length >= 3) {
        const id = parts.length >= 4 ? parts[0] : `P${index + 1}`;
        const coords = parts.length >= 4 ? parts.slice(1) : parts;
        
        points.push({
          id,
          x: parseFloat(coords[0]),
          y: parseFloat(coords[1]),
          z: parseFloat(coords[2])
        });
      }
    });
    
    if (points.length > 0) {
      onImportPoints(points);
      setImportText('');
      setShowImport(false);
    }
  };

  return (
    <motion.div 
      className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl p-6 shadow-xl"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sistema de coordenadas */}
        <div className="lg:w-1/4">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Sistema de Coordenadas
          </label>
          <select
            value={coordinateSystem}
            onChange={(e) => onCoordinateSystemChange(e.target.value)}
            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
          >
            <option value="geographic">Geográficas (WGS84)</option>
            <option value="utm29">UTM ETRS89 Zona 29N</option>
            <option value="utm30">UTM ETRS89 Zona 30N</option>
            <option value="utm31">UTM ETRS89 Zona 31N</option>
          </select>
        </div>

        {/* Entrada manual de puntos */}
        <div className="lg:w-1/2">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Agregar Punto Individual
          </label>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <input
                type="text"
                placeholder="ID"
                value={pointData.id}
                onChange={(e) => setPointData({...pointData, id: e.target.value})}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-sm"
              />
              <input
                type="number"
                step="any"
                placeholder="X"
                value={pointData.x}
                onChange={(e) => setPointData({...pointData, x: e.target.value})}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-sm"
                required
              />
              <input
                type="number"
                step="any"
                placeholder="Y"
                value={pointData.y}
                onChange={(e) => setPointData({...pointData, y: e.target.value})}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-sm"
                required
              />
              <input
                type="number"
                step="any"
                placeholder="Z"
                value={pointData.z}
                onChange={(e) => setPointData({...pointData, z: e.target.value})}
                className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-sm"
                required
              />
            </div>
            <motion.button
              type="submit"
              className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-4 h-4 inline mr-2" />
              Agregar Punto
            </motion.button>
          </form>
        </div>

        {/* Controles */}
        <div className="lg:w-1/4 space-y-3">
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Acciones
          </label>
          <motion.button
            onClick={() => setShowImport(!showImport)}
            className="w-full px-4 py-2 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            Importar
          </motion.button>
          
          <motion.button
            onClick={onExportPoints}
            className="w-full px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Download className="w-4 h-4 inline mr-2" />
            Exportar
          </motion.button>
          
          <motion.button
            onClick={onClearPoints}
            className="w-full px-4 py-2 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all duration-300"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RotateCcw className="w-4 h-4 inline mr-2" />
            Limpiar
          </motion.button>
        </div>
      </div>

      {/* Panel de importación */}
      {showImport && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-6 pt-6 border-t border-gray-200"
        >
          <label className="block text-sm font-semibold text-gray-700 mb-3">
            Importar Puntos (formato: ID X Y Z o X Y Z por línea)
          </label>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder="P1 123.456 456.789 10.5&#10;P2 124.456 457.789 11.2&#10;125.456 458.789 12.1"
            className="w-full h-32 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-none"
          />
          <div className="flex gap-3 mt-3">
            <motion.button
              onClick={handleImport}
              className="px-6 py-2 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Importar Puntos
            </motion.button>
            <motion.button
              onClick={() => setShowImport(false)}
              className="px-6 py-2 bg-gray-500 text-white rounded-xl font-semibold hover:bg-gray-600 transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancelar
            </motion.button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PointInput;